import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { fetchAllUsers, exportUsersToExcel, updateUserAccessLevel, fetchAccessLevels } from '../../services/api';
import toast from 'react-hot-toast';
import { IoClose, IoSaveOutline } from 'react-icons/io5';
import {
    IoShieldCheckmark,
    IoMail,
    IoChevronBack,
    IoChevronForward,
    IoSearchOutline,
    IoDownloadOutline,
    IoArrowUp,
    IoArrowDown,
    IoSettingsOutline, IoWalletOutline, IoPeopleOutline // Added new icon
} from 'react-icons/io5';
import { VscChromeClose } from "react-icons/vsc";
import { TokenUsageModal } from './TokenUsageModal';
import { CostSettingsModal } from './CostSettingsModal';
import McpOriginsManager from '../../components/McpConnections/McpManagement/McpOriginsManager';
import PreApprovedUserModal from './PreApprovedUserModal';
import Pagination from './Pagination';
import { RolesPermissionsPanel } from '../../components/admin/RolesPermissionsPanel';
import { UserRolesModal } from '../../components/admin/UserRolesModal';

const COST_SETTINGS_KEY = 'adminCostSettings';
const DEFAULT_COSTS = {
    tasks: { input: 0.5, output: 1.5 }, // Example default costs
    workflows: { input: 1.0, output: 3.0 },
};

// --- HELPER HOOK & FUNCTIONS (Unchanged) ---
// NEW: UI colors remain on the frontend, as they are a view concern.
const ACCESS_LEVEL_COLORS = {
    basic: 'bg-gray-500/10 text-gray-400',
    free: 'bg-green-500/10 text-green-400',
    paid: 'bg-[#5652CF]/10 text-blue-400',
    premium: 'bg-purple-500/10 text-purple-400',
    default: 'bg-gray-500/10 text-gray-400',
};

const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Intl.DateTimeFormat('en-US', {
        year: 'numeric', month: 'short', day: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    }).format(new Date(dateString));
};

export const AccessLevelModal = ({ isOpen, onClose, user, onSave, accessLevels }) => {
    const [selectedLevel, setSelectedLevel] = useState(user?.access_level || 'basic');
    const [isSaving, setIsSaving] = useState(false);

    useEffect(() => {
        if (user) {
            setSelectedLevel(user.access_level || 'basic');
        }
    }, [user]);

    // Render nothing if the modal is not open or config hasn't loaded
    if (!isOpen || !user || !accessLevels) return null;

    const handleSave = async () => {
        setIsSaving(true);
        await onSave(user.id || user._id || user.user_id, selectedLevel);
        setIsSaving(false);
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-60 backdrop-blur-sm">
            <div className="bg-1 rounded-xl shadow-2xl w-full max-w-md m-4 border border-line animate-fade-in-up">
                <div className="flex items-center justify-between p-4 border-b border-line">
                    <h3 className="text-lg font-bold text-p-text">Edit Access Level</h3>
                    <button onClick={onClose} className="p-1 rounded-full hover:bg-2 transition-colors">
                        <IoClose className="h-5 w-5 text-s-text" />
                    </button>
                </div>
                <div className="p-6 space-y-4">
                    <p className="text-sm text-s-text">
                        Change access for <strong className="text-p-text">{user.name}</strong> ({user.email}).
                    </p>
                    <div>
                        <label htmlFor="accessLevel" className="block mb-2 text-sm font-medium text-p-text">
                            Access Level
                        </label>
                        <select
                            id="accessLevel"
                            value={selectedLevel}
                            onChange={(e) => setSelectedLevel(e.target.value)}
                            className="w-full bg-2 border border-line rounded-lg px-3 py-2 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
                        >
                            {/* FIXED: Iterate over the `accessLevels` prop passed from AdminPage */}
                            {Object.entries(accessLevels).map(([key, config]) => (
                                <option key={key} value={key} className="capitalize bg-1 text-p-text">
                                    {config.label}
                                </option>
                            ))}
                        </select>
                        {/* FIXED: Display description from the fetched config */}
                        <p className="text-xs text-s-text mt-2 h-4">
                            {accessLevels[selectedLevel]?.description}
                        </p>
                    </div>
                </div>
                <div className="flex items-center justify-end p-4 bg-2 rounded-b-xl space-x-3">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-sm font-medium text-s-text bg-transparent border border-line rounded-lg hover:bg-1 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleSave}
                        disabled={isSaving}
                        className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-wait transition-colors"
                    >
                        <IoSaveOutline />
                        {isSaving ? 'Saving...' : 'Save Changes'}
                    </button>
                </div>
            </div>
        </div>
    );
};

/**
 * AdminInfo Component
 * Displays the current admin's information.
 */
const AdminInfo = ({ userInfo }) => {
    if (!userInfo) return null;
    return (
        <div className="bg-1 p-4 sm:p-5 rounded-xl shadow-lg mb-6">
            <h2 className="text-xl font-bold mb-4 text-p-text">Admin Information</h2>
            <div className="flex items-center gap-4">
                <img
                    src={userInfo.picture}
                    alt={userInfo.name}
                    className="rounded-full h-14 w-14 sm:h-16 sm:w-16 object-cover border-2 border-primary-gradient"
                />
                <div>
                    <h3 className="text-lg font-semibold text-p-text flex items-center gap-2">
                        {userInfo.name} <IoShieldCheckmark className="text-green-400" />
                    </h3>
                    <p className="text-sm text-s-text flex items-center gap-2">
                        <IoMail /> {userInfo.email}
                    </p>
                </div>
            </div>
        </div>
    );
};

/**
 * Responsive User Card for Mobile View
 * Displays user data in a card format for small screens.
 */
const UserCard = ({ user, onOpenUsageModal, onOpenAccessModal, onOpenRolesModal, accessLevelUiConfig }) => (
    <div className="bg-1 p-4 rounded-lg shadow-md border border-line flex flex-col gap-3">
        <div className="flex items-center gap-3">
            <img src={user.picture} alt={user.name} className="h-10 w-10 rounded-full" />
            <div className="flex-1">
                <p className="font-semibold text-p-text">{user.name}</p>
                <p className="text-xs text-s-text">{user.email}</p>
            </div>
        </div>
        <div className="border-t border-line pt-3 text-xs text-s-text space-y-2">
            <p className="flex justify-between items-center">
                <span>Created:</span>
                <span className="font-medium text-p-text">{formatDate(user.created_at)}</span>
            </p>
            <p className="flex justify-between items-center">
                <span>Last Login:</span>
                <span className="font-medium text-p-text">{formatDate(user.last_login_at)}</span>
            </p>
            <p className="flex justify-between items-center">
                <span>Auth Method:</span>
                <span className="font-medium text-p-text capitalize">{user.authentication_method}</span>
            </p>
            <p className="flex justify-between items-center">
                <span>Enterprise:</span>
                {user.enterprise_access ? (
                    <span className="bg-green-500/10 text-green-400 text-xs font-medium px-2.5 py-0.5 rounded-full">Yes</span>
                ) : (
                    <span className="bg-red-500/10 text-red-400 text-xs font-medium px-2.5 py-0.5 rounded-full">No</span>
                )}
            </p>
            {/* NEW: Display Access Level in Card */}
            {/* FIXED: Display Access Level using the config prop */}
            <p className="flex justify-between items-center">
                <span>Access:</span>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-semibold ${accessLevelUiConfig[user.access_level]?.color || 'bg-gray-500/10 text-gray-400'}`}>
                    {accessLevelUiConfig[user.access_level]?.label || 'Basic'}
                </span>
            </p>
        </div>
        {/* --- ACTION BUTTONS --- */}
        <div className="mt-2 pt-3 border-t border-line flex flex-col gap-2">
            <button
                onClick={() => onOpenUsageModal(user)}
                className="w-full flex items-center justify-center gap-2 text-center px-4 py-2 bg-[#5652CF]/10 text-blue-400 rounded-lg text-sm font-semibold hover:bg-[#5652CF]/20 transition-colors"
            >
                <IoWalletOutline />
                View Costs
            </button>
            <div className="flex gap-2">
                <button
                    onClick={() => onOpenRolesModal(user)}
                    className="flex-1 flex items-center justify-center gap-2 text-center px-4 py-2 bg-indigo-500/10 text-indigo-400 rounded-lg text-sm font-semibold hover:bg-indigo-500/20 transition-colors"
                >
                    <IoShieldCheckmark />
                    Roles
                </button>
                <button
                    onClick={() => onOpenAccessModal(user)}
                    className="flex-1 flex items-center justify-center gap-2 text-center px-4 py-2 bg-purple-500/10 text-purple-400 rounded-lg text-sm font-semibold hover:bg-purple-500/20 transition-colors"
                >
                    <IoSettingsOutline />
                    Access
                </button>
            </div>
        </div>
    </div>
);

/**
 * FilterControls Component
 * Houses all the new UI controls for search, filter, and export.
 */
const FilterControls = ({ searchTerm, setSearchTerm, enterpriseOnly, setEnterpriseOnly, onExport, isExporting }) => (
    <div className="mb-6 p-4 bg-1 rounded-xl shadow-lg flex flex-col md:flex-row items-center gap-4">
        <div className="relative w-full md:flex-1">
            <IoSearchOutline className="absolute left-3 top-1/2 -translate-y-1/2 text-s-text" />
            <input
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full bg-2 border border-line rounded-lg pl-10 pr-4 py-2 text-p-text focus:outline-none focus:ring-2 focus:ring-[#5652CF]"
            />
        </div>
        <div className="flex w-full md:w-auto items-center justify-between md:justify-start gap-4">
            <label className="flex items-center cursor-pointer select-none">
                <input
                    type="checkbox"
                    checked={enterpriseOnly}
                    onChange={(e) => setEnterpriseOnly(e.target.checked)}
                    className="sr-only"
                />
                <div className={`w-11 h-6 flex items-center rounded-full p-1 duration-300 ${enterpriseOnly ? 'bg-green-500' : 'bg-2'}`}>
                    <div className={`bg-white w-4 h-4 rounded-full shadow-md transform duration-300 ${enterpriseOnly ? 'translate-x-5' : ''}`}></div>
                </div>
                <span className="ml-3 text-sm font-medium text-p-text">Enterprise Only</span>
            </label>
            <button
                onClick={onExport}
                disabled={isExporting}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/20 text-green-400 rounded-lg hover:bg-green-500/30 disabled:opacity-50 disabled:cursor-wait transition-colors"
            >
                <IoDownloadOutline />
                {isExporting ? 'Exporting...' : 'Export'}
            </button>
        </div>
    </div>
);


/**
 * UsersList Component - UPDATED with Multi-Sort UI
 * Renders a responsive table with advanced sortable headers.
 */
const UsersList = ({ users, loading, sortConfig, onSort, onOpenUsageModal, onOpenAccessModal, onOpenRolesModal, accessLevelUiConfig }) => {
    if (loading) {
        return (
            <div className="space-y-4">
                {[...Array(6)].map((_, i) => (
                    <div key={i} className="bg-1 p-4 rounded-lg shadow-md animate-pulse">
                        <div className="flex items-center gap-3">
                            <div className="h-10 w-10 rounded-full bg-2"></div>
                            <div className="flex-1 space-y-2">
                                <div className="h-4 bg-2 rounded w-3/4"></div>
                                <div className="h-3 bg-2 rounded w-1/2"></div>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        );
    }
    if (!users?.length) return <div className="text-center p-8 bg-1 rounded-lg">No users found.</div>;

    // NEW: Function to get sort icon and order badge
    const getSortInfo = (key) => {
        const sortIndex = sortConfig.findIndex(s => s.key === key);
        if (sortIndex === -1) {
            return null; // Not being sorted
        }
        const sortItem = sortConfig[sortIndex];
        return {
            direction: sortItem.direction,
            order: sortIndex + 1,
        };
    };

    const headers = [
        { key: 'name', label: 'User' },
        { key: 'created_at', label: 'Created At' },
        { key: 'last_login_at', label: 'Last Login' },
        { key: 'authentication_method', label: 'Auth Method' },
        { key: 'enterprise_access', label: 'Enterprise' },
        { key: 'actions', label: 'Actions', sortable: false }, 
        { key: 'access_level', label: 'Access Level', sortable: false }
    ];

    return (
        <div className="bg-1 rounded-xl shadow-lg overflow-hidden">
            {/* Mobile View */}
            <div className="md:hidden p-4 space-y-4">
                {users.map(user => <UserCard key={user.email} user={user} onOpenUsageModal={onOpenUsageModal} accessLevelUiConfig={accessLevelUiConfig}  onOpenAccessModal={onOpenAccessModal} onOpenRolesModal={onOpenRolesModal} />)}
            </div>

            {/* Desktop View with Multi-Sort UI */}
            <div className="hidden md:block overflow-x-auto">
                <table className="w-full text-sm text-left text-s-text">
                    <thead className="text-xs text-p-text uppercase bg-2">
                        <tr>
                            {headers.map(header => {
                                const sortInfo = getSortInfo(header.key);
                                return (
                                    <th key={header.key} scope="col" className="px-6 py-3">
                                        <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => {
                                            if (header.sortable !== false) {
                                                onSort(header.key);
                                            }
                                        }}>
                                            {header.label}
                                            {sortInfo ? (
                                                <span className="flex items-center gap-1 text-white bg-[#5652CF] px-2 py-0.5 rounded-md">
                                                    <span>{sortInfo.order}</span>
                                                    {sortInfo.direction === 'asc' ? <IoArrowUp /> : <IoArrowDown />}
                                                </span>
                                            ) : (
                                                header.sortable !== false && <VscChromeClose className="text-gray-400 opacity-20" />
                                            )}
                                        </div>
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {users.map(user => (
                            <tr key={user.email} className="bg-1 border-b border-line hover:bg-2 transition-colors duration-200">
                                <td className="px-6 py-4 font-medium text-p-text whitespace-nowrap">
                                    <div className="flex items-center gap-3">
                                        <img src={user.picture} alt={user.name} className="h-9 w-9 rounded-full object-cover" />
                                        <div>
                                            <div>{user.name}</div>
                                            <div className="text-xs text-s-text">{user.email}</div>
                                        </div>
                                    </div>
                                </td>
                                <td className="px-6 py-4">{formatDate(user.created_at)}</td>
                                <td className="px-6 py-4">{formatDate(user.last_login_at)}</td>
                                <td className="px-6 py-4 capitalize">{user.authentication_method}</td>
                                <td className="px-6 py-4">
                                    {user.enterprise_access ?
                                        <span className="bg-green-500/10 text-green-400 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">Yes</span> :
                                        <span className="bg-red-500/10 text-red-400 text-xs font-medium me-2 px-2.5 py-0.5 rounded-full">No</span>
                                    }
                                </td>
                                <td className="px-6 py-4">
                                    <div className="flex items-center gap-2">
                                        <button onClick={() => onOpenUsageModal(user)} className="font-medium hover:opacity-80 transition-opacity" title="View Costs">
                                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium bg-[#5652CF]/10 text-blue-400">
                                                <IoWalletOutline className="text-xs" /> Costs
                                            </span>
                                        </button>
                                        <button onClick={() => onOpenRolesModal(user)} className="font-medium hover:opacity-80 transition-opacity" title="Manage Roles">
                                            <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium bg-indigo-500/10 text-indigo-400">
                                                Roles
                                            </span>
                                        </button>
                                    </div>
                                </td>
                                <td className="px-6 py-4">
                                    <button onClick={() => onOpenAccessModal(user)} className="font-medium hover:opacity-80 transition-opacity" title="Edit Access Level">
                                        {/* FIXED: Display Access Level using the config prop */}
                                        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-1 text-xs font-medium ${accessLevelUiConfig[user.access_level]?.color || 'bg-gray-500/10 text-gray-400'}`}>
                                            {accessLevelUiConfig[user.access_level]?.label || 'Basic'}
                                        </span>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

/**
 * Main AdminPage Component
 * Manages all state and logic, now with advanced multi-field sorting.
 */

const AdminPage = ({ userIdToken, userInfo }) => {
    const [usersData, setUsersData] = useState({ users: [], total_pages: 0, total_users: 0 });
    const [loading, setLoading] = useState(true);
    const [isExporting, setIsExporting] = useState(false);

    // --- STATE MANAGEMENT ---
    const [userRefreshKey, setUserRefreshKey] = useState(0); // Key to force re-fetching user data
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [enterpriseOnly, setEnterpriseOnly] = useState(false);

    // NEW: Sort state is now an array to handle multiple fields
    const [sortConfig, setSortConfig] = useState([
        { key: 'created_at', direction: 'desc' },
        { key: 'last_login_at', direction: 'desc' }
    ]);
    // --- New states for modals and costs ---
    const [isUsageModalOpen, setIsUsageModalOpen] = useState(false);
    const [isCostModalOpen, setIsCostModalOpen] = useState(false);
    const [selectedUserForUsage, setSelectedUserForUsage] = useState(null);
    const [costSettings, setCostSettings] = useState(DEFAULT_COSTS);

    // +++ NEW: STATE FOR BACKEND-DRIVEN CONFIG +++
    const [accessLevelsConfig, setAccessLevelsConfig] = useState(null);
    const [isConfigLoading, setIsConfigLoading] = useState(true);

    // +++ NEW: STATE FOR MODALS +++
    const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
    const [selectedUserForAccess, setSelectedUserForAccess] = useState(null);

    // +++ NEW: State for the pre-approved user modal +++
    const [isPreApprovedModalOpen, setIsPreApprovedModalOpen] = useState(false);

    // +++ NEW: State for Roles Modal +++
    const [isRolesModalOpen, setIsRolesModalOpen] = useState(false);
    const [selectedUserForRoles, setSelectedUserForRoles] = useState(null);

    // +++ NEW: State for Tab Navigation +++
    const [activeTab, setActiveTab] = useState('users');
    // NEW: Dynamically generate the UI config from the fetched data
    const accessLevelUiConfig = useMemo(() => {
        if (!accessLevelsConfig) return {};
        return Object.keys(accessLevelsConfig).reduce((acc, key) => {
            acc[key] = {
                label: accessLevelsConfig[key].label || key,
                color: ACCESS_LEVEL_COLORS[key] || ACCESS_LEVEL_COLORS.default,
            };
            return acc;
        }, {});
    }, [accessLevelsConfig]);

    // NEW: Fetch configuration on initial load
    useEffect(() => {
        const loadAdminConfig = async () => {
            if (userIdToken) {
                try {
                    setIsConfigLoading(true);
                    const config = await fetchAccessLevels(userIdToken);
                    setAccessLevelsConfig(config);
                } catch (error) {
                    toast.error("Failed to load admin configuration.");
                    console.error("Config fetch error:", error);
                } finally {
                    setIsConfigLoading(false);
                }
            }
        };
        loadAdminConfig();
    }, [userIdToken]);

    // Load cost settings from localStorage on initial render
    useEffect(() => {
        const savedSettings = JSON.parse(localStorage.getItem(COST_SETTINGS_KEY));
        if (savedSettings) {
            setCostSettings(savedSettings);
        } else {
            // If no settings saved, save the defaults
            localStorage.setItem(COST_SETTINGS_KEY, JSON.stringify(DEFAULT_COSTS));
        }
    }, []);

    const debouncedSearchTerm = useDebounce(searchTerm, 500);
    const PAGE_LIMIT = 10;

    // --- DATA FETCHING ---
    const loadAllUsers = useCallback(async (page, search, sort, enterprise) => {
        if (!userIdToken) return;
        setLoading(true);
        try {
            const params = { page, limit: PAGE_LIMIT, search, sortConfig: sort, enterpriseOnly: enterprise };
            const data = await fetchAllUsers(userIdToken, params);
            setUsersData(data);
        } catch (error) {
            toast.error(`Failed to fetch users: ${error.message || 'Unknown error'}`);
            setUsersData({ users: [], total_pages: 0, total_users: 0 });
        } finally {
            setLoading(false);
        }
    }, [userIdToken]);

    useEffect(() => {
        if (userIdToken) {
            loadAllUsers(currentPage, debouncedSearchTerm, sortConfig, enterpriseOnly);
        }
    }, [userRefreshKey]);

    // Effect for handling filters, search, and sorting changes
    useEffect(() => {
        // Reset to page 1 whenever filters change
        setCurrentPage(1);
        loadAllUsers(1, debouncedSearchTerm, sortConfig, enterpriseOnly);
    }, [debouncedSearchTerm, sortConfig, enterpriseOnly, loadAllUsers]);

    // Effect for handling page changes separately
    useEffect(() => {
        loadAllUsers(currentPage, debouncedSearchTerm, sortConfig, enterpriseOnly);
    }, [currentPage]);

    // --- HANDLERS ---
    const handlePageChange = (newPage) => {
        if (newPage > 0 && newPage <= usersData.total_pages) {
            setCurrentPage(newPage);
        }
    };

    // NEW: Advanced sort handler with 3 states (desc -> asc -> off)
    const handleSort = (key) => {
        setSortConfig(prevConfig => {
            const newConfig = [...prevConfig];
            const sortIndex = newConfig.findIndex(s => s.key === key);

            if (sortIndex !== -1) {
                // Field exists, move to next state
                if (newConfig[sortIndex].direction === 'desc') {
                    // State 1 (desc) -> State 2 (asc)
                    newConfig[sortIndex].direction = 'asc';
                } else {
                    // State 2 (asc) -> State 3 (off)
                    newConfig.splice(sortIndex, 1);
                }
            } else {
                // Field does not exist, add it
                // State 0 (off) -> State 1 (desc)
                newConfig.push({ key, direction: 'desc' });
            }
            return newConfig;
        });
    };

    const handleExport = async () => {
        setIsExporting(true);
        try {
            await exportUsersToExcel(userIdToken);
            toast.success('Export started! Your download will begin shortly.');
        } catch (error) {
            toast.error(`Export failed: ${error.message || 'Please try again.'}`);
        } finally {
            setIsExporting(false);
        }
    };

    // --- New Handlers for Modals ---
    const handleOpenUsageModal = (user) => {
        setSelectedUserForUsage(user);
        setIsUsageModalOpen(true);
    };

    const handleCloseUsageModal = () => {
        setIsUsageModalOpen(false);
        setSelectedUserForUsage(null);
    };
    
    const handleSaveCosts = (newSettings) => {
        setCostSettings(newSettings);
    };

    // +++ NEW: HANDLERS FOR ACCESS LEVEL MODAL +++
    const handleOpenAccessModal = (user) => {
        setSelectedUserForAccess(user);
        setIsAccessModalOpen(true);
    };

    const handleCloseAccessModal = () => {
        setIsAccessModalOpen(false);
        setSelectedUserForAccess(null);
    };

    const handleSaveAccessLevel = async (userId, newLevel) => {
        const toastId = toast.loading('Updating access level...');
        try {
            const result = await updateUserAccessLevel(userIdToken, userId, newLevel);
            
            // Optimistically update the UI without a full refresh
            setUsersData(prevData => ({
                ...prevData,
                users: prevData.users.map(user =>
                    (user.id || user._id || user.user_id) === userId ? { ...user, access_level: newLevel } : user
                ),
            }));

            toast.success('Access level updated!', { id: toastId });
            handleCloseAccessModal(); // Close modal on success
        } catch (error) {
            toast.error(`Failed to update: ${error.message || 'Unknown error'}`, { id: toastId });
        } finally {
            setUserRefreshKey(prev => prev + 1); // Trigger re-fetch
        }
    };

    // +++ NEW: HANDLERS FOR ROLES MODAL +++
    const handleOpenRolesModal = (user) => {
        setSelectedUserForRoles(user);
        setIsRolesModalOpen(true);
    };

    const handleCloseRolesModal = () => {
        setIsRolesModalOpen(false);
        setSelectedUserForRoles(null);
    };


    if (isConfigLoading) {
        return <div className="flex items-center justify-center h-screen bg-body text-p-text">Loading Configuration...</div>;
    }


    return (
        <div className="p-4 sm:p-6 md:p-8 bg-body min-h-screen text-p-text">
            <div className="max-w-7xl mx-auto">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6">
                    <h1 className="text-3xl font-bold">Admin Control Panel</h1>
                    <p className="text-s-text mt-1 sm:mt-0">Manage and monitor all user activity.</p>
                </div>

                <AdminInfo userInfo={userInfo} />

                <div className="mt-6 border-b border-line mb-6">
                    <nav className="-mb-px flex space-x-8">
                        <button
                            onClick={() => setActiveTab('users')}
                            className={`${activeTab === 'users' ? 'border-[#5652CF] text-[#5652CF]' : 'border-transparent text-s-text hover:text-p-text hover:border-line'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-lg transition-colors`}
                        >
                            Users
                        </button>
                        <button
                            onClick={() => setActiveTab('roles')}
                            className={`${activeTab === 'roles' ? 'border-[#5652CF] text-[#5652CF]' : 'border-transparent text-s-text hover:text-p-text hover:border-line'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-lg transition-colors`}
                        >
                            <span className="flex items-center gap-2">
                                <IoShieldCheckmark />
                                Roles & Permissions
                            </span>
                        </button>
                        <button
                            onClick={() => setActiveTab('mcp')}
                            className={`${activeTab === 'mcp' ? 'border-[#5652CF] text-[#5652CF]' : 'border-transparent text-s-text hover:text-p-text hover:border-line'} whitespace-nowrap pb-4 px-1 border-b-2 font-medium text-lg transition-colors`}
                        >
                            MCP Connections
                        </button>
                    </nav>
                </div>

                {activeTab === 'users' && (
                    <div className="mt-8 animate-fade-in">
                        <div className="flex justify-between items-center mb-4">
                            <h2 className="text-2xl font-bold">User Management</h2>
                            <div className="flex items-center gap-2">
                                {/* +++ NEW: Button to open the pre-approved user manager +++ */}
                                <button
                                    onClick={() => setIsPreApprovedModalOpen(true)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-2 border border-line rounded-lg hover:bg-1 transition-colors"
                                    title="Manage Pre-Approved Users"
                                >
                                    <IoPeopleOutline />
                                    <span>Pre-Approved</span>
                                </button>
                                <button
                                    onClick={() => setIsCostModalOpen(true)}
                                    className="flex items-center gap-2 px-3 py-2 text-sm bg-2 border border-line rounded-lg hover:bg-1 transition-colors"
                                    title="Configure Token Costs"
                                >
                                    <IoSettingsOutline />
                                    <span>Cost Settings</span>
                                </button>
                            </div>
                        </div>
                        <FilterControls
                            searchTerm={searchTerm}
                            setSearchTerm={setSearchTerm}
                            enterpriseOnly={enterpriseOnly}
                            setEnterpriseOnly={setEnterpriseOnly}
                            onExport={handleExport}
                            isExporting={isExporting}
                        />
                        <UsersList
                            users={usersData.users}
                            loading={loading}
                            sortConfig={sortConfig}
                            onSort={handleSort}
                            onOpenUsageModal={handleOpenUsageModal} // Pass the handler
                            onOpenAccessModal={handleOpenAccessModal} // Pass the handler down
                            onOpenRolesModal={handleOpenRolesModal} // Pass the Roles handler down
                            // Pass the dynamically generated config to the list
                            accessLevelUiConfig={accessLevelUiConfig}
                        />
                        <Pagination
                            currentPage={usersData.current_page || 1}
                            totalPages={usersData.total_pages}
                            totalUsers={usersData.total_users}
                            limit={PAGE_LIMIT}
                            onPageChange={handlePageChange}
                        />
                    </div>
                )}

                {/* +++ NEW ROLES & PERMISSIONS SECTION +++ */}
                {activeTab === 'roles' && (
                    <div className="animate-fade-in">
                        <RolesPermissionsPanel userIdToken={userIdToken} />
                    </div>
                )}

                {/* +++ NEW MCP MANAGEMENT SECTION +++ */}
                {activeTab === 'mcp' && (
                    <div className="animate-fade-in">
                        <McpOriginsManager userIdToken={userIdToken} />
                    </div>
                )}
            </div>
            {/* Render Both Modals */}
            <CostSettingsModal
                isOpen={isCostModalOpen}
                onClose={() => setIsCostModalOpen(false)}
                onSave={handleSaveCosts}
            />
            {selectedUserForUsage && (
                <TokenUsageModal 
                    isOpen={isUsageModalOpen}
                    onClose={handleCloseUsageModal}
                    user={selectedUserForUsage}
                    userIdToken={userIdToken}
                    costSettings={costSettings}
                />
            )}
            {/* +++ NEW: Render the PreApprovedUserModal +++ */}
            <PreApprovedUserModal
                isOpen={isPreApprovedModalOpen}
                onClose={() => setIsPreApprovedModalOpen(false)}
                userIdToken={userIdToken}
                accessLevelsConfig={accessLevelUiConfig}
            />

            {/* +++ NEW: RENDER THE ACCESS LEVEL MODAL +++ */}
            {selectedUserForAccess && (
                <AccessLevelModal
                    isOpen={isAccessModalOpen}
                    onClose={handleCloseAccessModal}
                    user={selectedUserForAccess}
                    onSave={handleSaveAccessLevel}
                    // Pass the raw fetched config to the modal
                    accessLevels={accessLevelsConfig}
                />
            )}

            {/* +++ NEW: RENDER THE USER ROLES MODAL +++ */}
            {selectedUserForRoles && (
                <UserRolesModal
                    isOpen={isRolesModalOpen}
                    onClose={handleCloseRolesModal}
                    user={selectedUserForRoles}
                    userIdToken={userIdToken}
                />
            )}
        </div>
    );
};

export default AdminPage;